import { ArrowLeftOutlined } from '@ant-design/icons';
import { Breadcrumb, Divider, Spin } from 'antd';
import React from 'react';
import { batch, connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { enrollmentApi } from '../../../api/';
import constants from '../../../constants';
import { getFullVelocityUrl } from '../../../Helper';
import { PersonDetailModel, PivProfileResponse, PivValidation } from '../../../model/EnrollmentModel';
import { StoreEvent } from '../../../store';
import {
	setDisplayAddViaSmartCard,
	setFetchingScanData,
	setHavePersonChanged,
	setIsEnrollmentTabLoading,
	setPersonInformationId,
} from '../../../store/enrollment/actions';
import { RootReducer } from '../../../store/rootReducer';
import { PIVCardInfo, PIVHeader } from '../../enrollment';
import { enrollmentGoBackTextId } from '../helper';
import styles from './pivContainer.module.scss';

// Internal state for the component
interface State {
	isLoading: boolean;
	hasChange: boolean;
	validationResult: PivValidation;
	validationProfileResponse: PivProfileResponse;
}

// Props those being mapped from Store
interface StateProps {
	fetchingScanData: boolean;
	displayAddViaSmartCard: boolean;
	personId: number;
	fullName: string;
}

// Dispatchable methods (invoke our store's actions)
interface DispatchProps {
	handleFetchScanData: () => void;
	handleReturnToEnrollmentInAddPIV: () => void;
	handleReturnToEnrollmentInUpdatePIV: () => void;
}

// Normal properties for the component
interface OwnProps {
	goBackText: string;
	setUpdatePivPersonData?: (newValue: PersonDetailModel) => void;
}

// combine them together
type Props = StateProps & DispatchProps & OwnProps;

class component extends React.Component<Props, State> {
	constructor(prop: Props) {
		super(prop);

		//We use this flag to avoid multiple api calls at the same time
		window.sessionStorage.setItem(constants.sessionStorage.enrollment.RETRIEVE_ENROLLMENT_FLAG, '0');

		this.state = {
			isLoading: true,
			hasChange: false,
			validationResult: null,
			validationProfileResponse: null,
		};
	}

	componentDidMount() {
		enrollmentApi
			.retrieveValidationProfiles()
			.then(response => {
				this.setState({
					isLoading: false,
					hasChange: true,
					validationProfileResponse: response,
				});
			})
			.catch(e => console.log(e));
	}

	componentDidCatch(error, errorInfo) {
		console.log(error, errorInfo);
	}

	shouldComponentUpdate(nextProps, nextState) {
		const { hasChange } = nextState;
		if (!hasChange) {
			this.fetchLoop(nextProps.fetchingScanData);
			return false;
		}

		return true;
	}

	componentDidUpdate() {
		// this will call action after every update
		// as every time this action is called it passes in a new reference this.props.loading to this component
		// so react will update this component again, causing the action to be called again
		// ... endless loop
		this.fetchLoop(this.props.fetchingScanData);
	}

	componentWillUnmount(): void {
		this.props.handleFetchScanData();
	}

	private fetchLoop = fetchingScanData => {
		if (fetchingScanData) {
			const sessionStorageKey = constants.sessionStorage.enrollment.RETRIEVE_ENROLLMENT_FLAG;
			if (window.sessionStorage.getItem(sessionStorageKey) === '0') {
				window.sessionStorage.setItem(sessionStorageKey, '1');

				enrollmentApi
					.retrievePivEnrollment()
					.then(data => {
						//Status = 1 means there was an update and we need to redirect to enrollment screen
						if (data.FetchStatus === 1) {
							const velocityUrl = getFullVelocityUrl();
							const url = `${velocityUrl}Enrollment/EnrollmentManager`;
							window.location.href = url;
						} else {
							//Status 2 means we had a record on the DB and we will process that record to re-render screen
							const hasChange = data.FetchStatus === 2;
							if (hasChange) {
								this.props.handleFetchScanData();
							}

							window.sessionStorage.setItem(sessionStorageKey, '0');
							this.setState({
								isLoading: false,
								hasChange: hasChange,
								validationResult: data,
							});
						}
					})
					.catch(e => console.log(e));
			}
		}
	};

	render() {
		const { fetchingScanData, goBackText, personId, fullName, setUpdatePivPersonData } = this.props;
		const { isLoading, validationResult, validationProfileResponse } = this.state;
		const showCreatePerson = !fetchingScanData && validationResult && validationResult.CertCheckResponse.OKToAcceptCard;
		const velocityUrl = getFullVelocityUrl();
		const downloadPivReaderUrl = `${velocityUrl}Enrollment/DownloadWebPIVReader`;

		const enrollmentPIVReaderDownload = (
			<div className={styles.optionsContainer}>
				<span>
					{_('EnrollmentPIVReaderDownload')} <a href={downloadPivReaderUrl}>{_('DownloadNow')}</a>
				</span>
			</div>
		);

		const handleOnClickBack = e => {
			if (personId !== 0) {
				this.props.handleReturnToEnrollmentInUpdatePIV();
			} else {
				this.props.handleReturnToEnrollmentInAddPIV();
			}
		};

		const handleOnGoBackToPerson = e => {
			this.props.handleReturnToEnrollmentInAddPIV();
		};

		const breadCrumbTitle =
			personId === 0 ? (
				<Breadcrumb.Item>{_('AddPersonViaSmartCard')}</Breadcrumb.Item>
			) : (
				<>
					<Breadcrumb.Item className={styles.navigationItem}>
						<a onClick={handleOnGoBackToPerson}>
							<label>{fullName}</label>
						</a>
					</Breadcrumb.Item>
					<Breadcrumb.Item>{_('UpdatePersonViaSmartCard')}</Breadcrumb.Item>
				</>
			);

		return (
			<div className={styles.container}>
				<div className={styles.navigationContainer}>
					<Breadcrumb separator='/' className={styles.breadcrumb}>
						<Breadcrumb.Item className={styles.navigationItem}>
							<a onClick={handleOnClickBack}>
								<ArrowLeftOutlined /> <label id={enrollmentGoBackTextId}>{goBackText}</label>
							</a>
						</Breadcrumb.Item>
						{breadCrumbTitle}
					</Breadcrumb>
				</div>
				<Spin tip={`${_('Loading')}...`} spinning={isLoading} size='default' className={styles.spinContainer}>
					<div className={styles.modalMock}>
						<p className={styles.modalTitle}>{_('VerifySmartCardInformation')}</p>
						<Divider className={styles.divider} />
						<PIVHeader
							validationProfileResponse={validationProfileResponse}
							validationResult={validationResult}
							showCreatePerson={showCreatePerson}
							downloadPivReaderUrl={downloadPivReaderUrl}
							setUpdatePivPersonData={setUpdatePivPersonData}
						/>
						<Divider className={styles.divider} />
						{!fetchingScanData && !validationResult && enrollmentPIVReaderDownload}
						{fetchingScanData && <span className={styles.instructions}>{_('EnrollmentInstructions')}</span>}
						{!fetchingScanData && validationResult && <PIVCardInfo validationResult={validationResult} />}
					</div>
				</Spin>
			</div>
		);
	}
}

//TODO: FIX THIS
const mapStateToProps = (states: RootReducer, ownProps: OwnProps): StateProps => {
	const personId: number = states.enrollmentReducer.personInformationId;
	const fullName =
		personId !== 0 ? `${states.enrollmentReducer.personInformationData.LastName}, ${states.enrollmentReducer.personInformationData.FirstName}` : '';
	return {
		fetchingScanData: states.enrollmentReducer.fetchingScanData,
		displayAddViaSmartCard: states.enrollmentReducer.displayAddViaSmartCard,
		personId: states.enrollmentReducer.personInformationId,
		fullName: fullName,
	};
};

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, StoreEvent>, ownProps: OwnProps): DispatchProps => {
	return {
		handleFetchScanData: async () => {
			await dispatch(setFetchingScanData(false));
		},
		handleReturnToEnrollmentInAddPIV: async () => {
			await dispatch(setDisplayAddViaSmartCard(false));
		},
		handleReturnToEnrollmentInUpdatePIV: async () => {
			await batch(() => {
				dispatch(setPersonInformationId(0));
				dispatch(setIsEnrollmentTabLoading(false));
				dispatch(setDisplayAddViaSmartCard(false));
				dispatch(setHavePersonChanged(false));
			});
		},
	};
};

const PIVContainer = connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(component);

export { PIVContainer };
